import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Link2, Unlink, Search, AlertTriangle, Clock } from 'lucide-react';
import Modal from './Modal';
import { apiClient } from '../lib/apiClient';
import { useStore } from '../store/useStore';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { computeProfitability, costBarColor, formatTracked } from '../lib/profitability';
import { useClickUpStatus, type ClickUpListInfo } from '../lib/clickupStatus';
import type { Project } from '../types';

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// ── List picker (searchable — the workspace has well over a hundred lists) ───

function ClickUpListPicker({
  open,
  clientName,
  onClose,
  onPick,
}: {
  open: boolean;
  clientName: string;
  onClose: () => void;
  onPick: (list: ClickUpListInfo) => void;
}) {
  const [lists, setLists] = useState<ClickUpListInfo[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const r = await apiClient.get<{ lists?: ClickUpListInfo[] }>(`/clickup?action=lists${refresh ? '&refresh=1' : ''}`);
      if (!Array.isArray(r?.lists)) throw new Error('Unexpected response from the ClickUp endpoint.');
      setLists(r.lists);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not load ClickUp lists.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !lists && !loading) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const matches = useMemo(() => {
    if (!lists) return [];
    const terms = norm(query).split(/\s+/).filter(Boolean);
    const client = norm(clientName);
    // Folders are per client — float the client's own folder to the top.
    const isClientFolder = (l: ClickUpListInfo) => {
      const folder = norm(l.folderName ?? '');
      return folder.length >= 3 && client.length >= 3 && (folder.includes(client) || client.includes(folder));
    };
    return lists
      .filter((l) => {
        const hay = norm(`${l.spaceName} ${l.folderName ?? ''} ${l.name}`);
        return terms.every((t) => hay.includes(t));
      })
      .sort((a, b) => Number(isClientFolder(b)) - Number(isClientFolder(a)))
      .slice(0, 200);
  }, [lists, query, clientName]);

  return (
    <Modal open={open} onClose={onClose} title="Link ClickUp list" size="md">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search lists, folders, spaces…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <button
            onClick={() => load(true)}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-700 border border-gray-200 rounded-lg disabled:opacity-50"
            title="Reload lists from ClickUp"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" /> {error}
          </div>
        )}
        {loading && !lists && <p className="py-8 text-center text-sm text-gray-400">Loading ClickUp lists…</p>}

        {lists && (
          <>
            <p className="text-xs text-gray-400">
              {matches.length === 200 ? 'First 200 of ' : ''}{matches.length === 200 ? '' : `${matches.length} of `}{lists.length} lists
            </p>
            <div className="max-h-[50vh] overflow-y-auto scrollbar-thin -mx-1 px-1 space-y-0.5">
              {matches.length === 0 && <p className="py-6 text-center text-sm text-gray-300">No matching lists</p>}
              {matches.map((l) => (
                <button
                  key={l.id}
                  onClick={() => onPick(l)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-brand-50 transition-colors"
                >
                  <p className="text-sm text-gray-800">{l.name}</p>
                  <p className="text-xs text-gray-400">
                    {l.spaceName}{l.folderName ? ` › ${l.folderName}` : ''}
                  </p>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Project card ─────────────────────────────────────────────────────────────

export default function ClickUpProfitCard({ project, clientName }: { project: Project; clientName: string }) {
  const { status } = useClickUpStatus();
  const currentUser = useStore((s) => s.currentUser);
  const updateProject = useStore((s) => s.updateProject);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const canEdit = !!currentUser && currentUser.role !== 'viewer';
  const value = Number(project.value);
  const linked = !!project.clickupListId;
  const synced = project.clickupSyncedAt != null;

  const syncNow = async () => {
    setBusy(true);
    setError('');
    try {
      await apiClient.post(`/clickup/sync/${project.id}`);
      await useStore.getState().refetchProjects();
    } catch (e) {
      setError((e as Error)?.message ?? 'Sync failed.');
    } finally {
      setBusy(false);
    }
  };

  const link = async (list: ClickUpListInfo) => {
    setPickerOpen(false);
    setBusy(true);
    setError('');
    try {
      const label = list.folderName ? `${list.folderName} › ${list.name}` : list.name;
      await updateProject(project.id, { clickupListId: list.id, clickupListName: label });
      await apiClient.post(`/clickup/sync/${project.id}`);
      await useStore.getState().refetchProjects();
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not link the list.');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    setError('');
    try {
      await updateProject(project.id, { clickupListId: null, clickupListName: null });
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not unlink.');
    } finally {
      setBusy(false);
    }
  };

  const p = status ? computeProfitability(value, project.clickupTrackedMs ?? 0, status.hourlyCost) : null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 mt-4">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Profitability · ClickUp time</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {linked ? project.clickupListName ?? project.clickupListId : 'Total project value vs. what the tracked time cost'}
          </p>
        </div>
        {canEdit && status?.configured && (
          <div className="flex items-center gap-1.5">
            {linked && (
              <button
                onClick={syncNow}
                disabled={busy}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> Sync now
              </button>
            )}
            <button
              onClick={() => setPickerOpen(true)}
              disabled={busy}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-brand-600 border border-brand-200 rounded-lg hover:bg-brand-50 disabled:opacity-50"
            >
              <Link2 size={12} /> {linked ? 'Change list' : 'Link ClickUp list'}
            </button>
            {linked && (
              <button
                onClick={unlink}
                disabled={busy}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-50"
                title="Unlink ClickUp list"
              >
                <Unlink size={13} />
              </button>
            )}
          </div>
        )}
      </div>

      {status && !status.configured && (
        <p className="text-sm text-gray-400">
          ClickUp isn't connected yet — the server needs a <code className="text-xs bg-gray-100 px-1 rounded">CLICKUP_API_TOKEN</code>.
        </p>
      )}

      {status?.configured && !linked && (
        <p className="text-sm text-gray-400">No ClickUp list linked. Link the project's list to see hours, cost and margin.</p>
      )}

      {status?.configured && linked && !synced && (
        <p className="text-sm text-gray-400">{busy ? 'Pulling hours from ClickUp…' : 'Not synced yet.'}</p>
      )}

      {p && status?.configured && linked && synced && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Hours tracked</p>
              <p className="text-xl font-bold text-gray-900">{formatTracked(project.clickupTrackedMs)}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {project.clickupTaskCount ?? 0} tasks
                {(project.clickupEstimateMs ?? 0) > 0 && ` · est. ${formatTracked(project.clickupEstimateMs)}`}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Cost of time</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(p.cost, project.currency)}</p>
              <p className="text-xs text-gray-400 mt-0.5">@ {formatCurrency(status.hourlyCost)}/h</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Margin</p>
              <p className={`text-xl font-bold ${p.margin < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(p.margin, project.currency)}
              </p>
              <p className={`text-xs mt-0.5 ${p.margin < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                {p.marginPercent != null ? `${Math.round(p.marginPercent * 100)}% of project value` : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Effective rate</p>
              <p className="text-xl font-bold text-gray-900">
                {p.effectiveRate != null ? `${formatCurrency(p.effectiveRate)}/h` : '—'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">project value ÷ hours</p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-400">Cost vs total project value</span>
              <span className={p.costRatio != null && p.costRatio > 1 ? 'text-red-600 font-semibold' : 'text-gray-500'}>
                {p.costRatio != null ? `${Math.round(p.costRatio * 100)}%` : '—'}
                {p.costRatio != null && p.costRatio > 1 && ' · over budget'}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${costBarColor(p.costRatio)}`}
                style={{ width: `${Math.min(p.costRatio ?? 0, 1) * 100}%` }}
              />
            </div>
          </div>
        </>
      )}

      {linked && (synced || project.clickupSyncError) && (
        <div className="flex items-center gap-3 flex-wrap mt-3 text-xs">
          {synced && (
            <span className="flex items-center gap-1 text-gray-400">
              <Clock size={11} /> Synced {formatDateTime(project.clickupSyncedAt as string)}
            </span>
          )}
          {project.clickupSyncError && (
            <span className="flex items-center gap-1 text-amber-600">
              <AlertTriangle size={11} /> Last sync failed: {project.clickupSyncError}{synced ? ' — showing previous numbers' : ''}
            </span>
          )}
        </div>
      )}

      {error && <p className="mt-3 text-xs text-red-500">{error}</p>}

      <ClickUpListPicker open={pickerOpen} clientName={clientName} onClose={() => setPickerOpen(false)} onPick={link} />
    </div>
  );
}
